import { useEffect, useMemo } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { OutdoorSpec } from "../scenario/types";
import { PalmBelt } from "./PalmBelt";

/**
 * Daylight exterior without Sky.js / Water.js. Those two objects were the
 * reason the pool scene looked grey AND ate >2 GB of Chrome RAM:
 *   - Sky.js is an HDR shader that ACES tone-mapping washes to white/grey,
 *     and PMREMGenerator.fromScene() of a 600-unit dome is expensive.
 *   - A cubemap painted as a real blue sky is both cheaper and actually blue.
 *
 * Opt-in per environment (`EnvironmentSpec.outdoor`).
 */

/** Unit vector toward the sun from elevation above horizon + compass bearing. */
export function sunVector(elevationDeg: number, azimuthDeg: number): THREE.Vector3 {
  const phi = THREE.MathUtils.degToRad(90 - elevationDeg);
  const theta = THREE.MathUtils.degToRad(azimuthDeg);
  return new THREE.Vector3().setFromSphericalCoords(1, phi, theta);
}

function skyColorAt(dir: THREE.Vector3, sun: THREE.Vector3): THREE.Color {
  const up = Math.max(dir.y, -1);
  const zenith = new THREE.Color("#3b90d6");
  const horizon = new THREE.Color("#cfe6f8");
  const ground = new THREE.Color("#6b8f44");
  const col = new THREE.Color();
  if (up > 0) {
    col.copy(horizon).lerp(zenith, Math.pow(up, 0.55));
  } else {
    col.copy(horizon).lerp(ground, Math.min(-up * 1.4, 1));
  }
  // Warm the sky near the sun so the disc reads, without blowing it to white.
  const towardSun = Math.max(0, dir.dot(sun));
  const glow = Math.pow(towardSun, 32) * 0.35;
  col.r = Math.min(1, col.r + glow * 0.9);
  col.g = Math.min(1, col.g + glow * 0.55);
  col.b = Math.min(1, col.b + glow * 0.15);
  return col;
}

/** Six tiny LDR faces. 64px is enough for IBL + a background; 256px was wasting RAM. */
function makeSkyCube(sun: THREE.Vector3, size = 64): THREE.CubeTexture {
  const faces: HTMLCanvasElement[] = [];
  const dirs: THREE.Vector3[] = [
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(-1, 0, 0),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, 0, 1),
    new THREE.Vector3(0, 0, -1),
  ];
  const ups: THREE.Vector3[] = [
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, 0, 1),
    new THREE.Vector3(0, 0, -1),
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, -1, 0),
  ];
  for (let f = 0; f < 6; f += 1) {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      faces.push(canvas);
      continue;
    }
    const img = ctx.createImageData(size, size);
    const forward = dirs[f];
    const up = ups[f];
    const right = new THREE.Vector3().crossVectors(up, forward).normalize();
    const dir = new THREE.Vector3();
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const u = (x / (size - 1)) * 2 - 1;
        const v = (y / (size - 1)) * 2 - 1;
        dir.copy(forward).addScaledVector(right, u).addScaledVector(up, v).normalize();
        const c = skyColorAt(dir, sun);
        const i = (y * size + x) * 4;
        img.data[i] = Math.round(c.r * 255);
        img.data[i + 1] = Math.round(c.g * 255);
        img.data[i + 2] = Math.round(c.b * 255);
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    faces.push(canvas);
  }
  const cube = new THREE.CubeTexture(faces);
  cube.colorSpace = THREE.SRGBColorSpace;
  cube.needsUpdate = true;
  return cube;
}

export function OutdoorSky({ spec }: { spec: OutdoorSpec }) {
  const { scene, gl } = useThree();

  const elevation = spec.sunElevationDeg ?? 40;
  const azimuth = spec.sunAzimuthDeg ?? 145;
  const shadowRadius = spec.shadowRadius ?? 20;

  const sun = useMemo(() => sunVector(elevation, azimuth), [elevation, azimuth]);

  useEffect(() => {
    const cube = makeSkyCube(sun);
    const pmrem = new THREE.PMREMGenerator(gl);
    const env = pmrem.fromCubemap(cube);
    const previousEnv = scene.environment;
    const previousBg = scene.background;
    scene.environment = env.texture;
    scene.background = cube;
    gl.shadowMap.enabled = true;
    gl.shadowMap.type = THREE.PCFSoftShadowMap;

    return () => {
      scene.environment = previousEnv;
      scene.background = previousBg;
      env.dispose();
      cube.dispose();
      pmrem.dispose();
    };
  }, [scene, gl, sun]);

  const ground = spec.ground;
  const groundSize = ground?.size ?? 90;
  const packGrassUrl = ground?.textureUrl;
  const groundTex = useMemo(() => {
    if (!ground) return null;
    // Only tile a paid-pack albedo. Do not invent a grass pattern.
    if (packGrassUrl) {
      const loader = new THREE.TextureLoader();
      const tex = loader.load(packGrassUrl);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.colorSpace = THREE.SRGBColorSpace;
      const n = ground.textureRepeat ?? 22;
      tex.repeat.set(n, n);
      return tex;
    }
    return null;
  }, [ground, packGrassUrl, ground?.textureRepeat]);
  useEffect(() => {
    return () => {
      groundTex?.dispose();
    };
  }, [groundTex]);

  const sunPos: [number, number, number] = [
    sun.x * 80,
    Math.max(sun.y * 80, 18),
    sun.z * 80,
  ];

  return (
    <>
      <directionalLight
        position={sunPos}
        intensity={spec.sunIntensity ?? 2.6}
        color="#fff1c8"
        castShadow={!!spec.shadows}
        shadow-mapSize={[1024, 1024]}
        shadow-bias={-0.0005}
        shadow-normalBias={0.04}
        shadow-camera-left={-shadowRadius}
        shadow-camera-right={shadowRadius}
        shadow-camera-top={shadowRadius}
        shadow-camera-bottom={-shadowRadius}
        shadow-camera-near={10}
        shadow-camera-far={220}
      />
      {/* Keep bounce light LOW so the sun actually casts readable shadows. */}
      <hemisphereLight args={["#b9d7f2", "#7a7348", 0.18]} />
      {ground ? (
        <mesh
          name="outdoor_ground"
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, ground.y ?? -0.04, 0]}
          receiveShadow={!!spec.shadows}
        >
          <planeGeometry args={[groundSize, groundSize]} />
          <meshStandardMaterial
            map={groundTex ?? undefined}
            color={groundTex ? "#ffffff" : (ground.color ?? "#4f7a38")}
            roughness={0.95}
            metalness={0}
          />
        </mesh>
      ) : null}
      {/* Distant hills so the horizon isn't a flat green band on any side. */}
      {(
        [
          [0, -6, -(spec.palms?.radius ?? 18) - 10],
          [0, -6, (spec.palms?.radius ?? 18) + 10],
          [-(spec.palms?.radius ?? 18) - 12, -7, 0],
          [(spec.palms?.radius ?? 18) + 12, -7, 0],
        ] as [number, number, number][]
      ).map((pos, i) => (
        <mesh
          key={`horizon_hill_${i}`}
          name={`horizon_hill_${i}`}
          position={pos}
          scale={[2.8, 0.22, 1.35]}
          receiveShadow
        >
          <sphereGeometry args={[28, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#4e7a33" roughness={1} />
        </mesh>
      ))}
      {spec.palms ? (
        <PalmBelt
          count={spec.palms.count ?? 36}
          radius={spec.palms.radius ?? 21}
          y={ground?.y ?? 0}
          pots={spec.palms.pots}
        />
      ) : null}
    </>
  );
}
